import React from "react";
import { I } from "./icons";
import { useSiteLocale } from "./flow_copy";

/* ── Share with a friend (owner add-on, 2026-10-13) ────────────────────────
 * One control, two placements (home hero + site footer). Where the Web
 * Share API exists it is used directly; everywhere else a small menu
 * offers the honest equivalents — copy the link, SMS, WhatsApp, email.
 * The shared message is the localized `shareText` plus the canonical
 * front-door URL: opening-soon framing, the $3.99 flat price, "loved
 * one" — never a live-ordering claim. Styles live in home.css (.cc-share*).
 */
export const SHARE_URL = "https://www.carecardexpress.com/";

export function shareMessage(t) {
  return `${t.shareText} ${SHARE_URL}`;
}

export function ShareWithFriend({ variant = "footer" }) {
  const { t } = useSiteLocale();
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open ]);

  const onShare = () => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      // A dismissed share sheet rejects — that is the visitor's choice,
      // not an error, and there is nothing to fall back to afterwards.
      navigator.share({ title: t.shareTitle, text: t.shareText, url: SHARE_URL }).catch(() => {});
      return;
    }
    setOpen((v) => !v);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => { setCopied(false); setOpen(false); }, 900);
    } catch {
      setOpen(false);
    }
  };

  const message = encodeURIComponent(shareMessage(t));
  const items = [
    { key: "sms", label: t.shareSms, Icon: I.MessageCircle, href: `sms:?&body=${message}` },
    { key: "whatsapp", label: t.shareWhatsapp, Icon: I.Send, href: `https://wa.me/?text=${message}`, external: true },
    { key: "email", label: t.shareEmail, Icon: I.Mail, href: `mailto:?subject=${encodeURIComponent(t.shareTitle)}&body=${message}` },
  ];

  return (
    <span className={`cc-share cc-share--${variant}`} ref={wrapRef}>
      <button
        type="button"
        className="cc-share-btn"
        onClick={onShare}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <I.Share2 size={17} aria-hidden="true" />
        {t.shareCta}
      </button>
      {open && (
        <span className="cc-share-menu" role="menu" aria-label={t.shareMenuLabel}>
          <button type="button" className="cc-share-item" role="menuitem" onClick={onCopy}>
            {copied ? <I.Check size={16} aria-hidden="true" /> : <I.Copy size={16} aria-hidden="true" />}
            {copied ? t.shareCopied : t.shareCopy}
          </button>
          {items.map(({ key, label, Icon, href, external }) => (
            <a
              key={key}
              className="cc-share-item"
              role="menuitem"
              href={href}
              {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
              onClick={() => setOpen(false)}
            >
              <Icon size={16} aria-hidden="true" />
              {label}
            </a>
          ))}
        </span>
      )}
    </span>
  );
}

export default ShareWithFriend;
